import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import AdminLayout from './AdminLayout';
import RichTextEditor from '../../components/RichTextEditor';
import FileUploader from '../../components/FileUploader';

const C = {
  blue: '#6699ff',
  blueDark: '#4d7ae6',
  navy: '#0b1120',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  creamPale: '#f8f5ef',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

const CATEGORIES = [
  'Industry News',
  'Technical Insights',
  'Case Studies',
  'Company Updates',
  'Research & Development',
  'Training & Education',
];

const labelStyle = {
  display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 2,
  textTransform: 'uppercase', color: C.textBody, marginBottom: 8,
};

const inputStyle = {
  width: '100%', padding: '14px 16px', border: `1px solid ${C.creamWarm}`,
  background: C.creamPale, fontFamily: "'Manrope', sans-serif", fontSize: 14,
  color: C.textDark, outline: 'none', transition: 'all 0.3s ease',
};

const focusHandler = (e) => { e.target.style.borderColor = C.blue; e.target.style.background = C.white; e.target.style.boxShadow = '0 0 0 3px rgba(102,153,255,0.08)'; };
const blurHandler = (e) => { e.target.style.borderColor = C.creamWarm; e.target.style.background = C.creamPale; e.target.style.boxShadow = 'none'; };

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [author, setAuthor] = useState('SPC Team');
  const [content, setContent] = useState(null);
  const [coverImage, setCoverImage] = useState('');
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [articleId, setArticleId] = useState(id || null);
  const [loading, setLoading] = useState(isEditing);

  useEffect(() => {
    if (isEditing) loadArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadArticle = async () => {
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      alert('Article not found');
      navigate('/spc-portal-1003');
      return;
    }

    setTitle(data.title);
    setSlug(data.slug);
    setExcerpt(data.excerpt || '');
    setCategory(data.category || '');
    setTags(data.tags?.join(', ') || '');
    setAuthor(data.author || 'SPC Team');
    setContent(data.content);
    setCoverImage(data.cover_image || '');
    setStatus(data.status);

    const { data: fileData } = await supabase
      .from('article_files')
      .select('*')
      .eq('article_id', id);

    if (fileData) setFiles(fileData);
    setLoading(false);
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!isEditing || !slug) setSlug(generateSlug(value));
  };

  const uploadCoverImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        alert('Cover image must be under 5MB');
        return;
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `covers/${Date.now()}_${safeName}`;

      const { error } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, { contentType: file.type });

      if (error) {
        alert('Upload failed: ' + error.message);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      setCoverImage(urlData.publicUrl);
    };

    input.click();
  };

  const saveArticle = async (publishStatus) => {
    if (!title.trim()) {
      alert('Title is required');
      return;
    }
    if (!slug.trim()) {
      alert('Slug is required');
      return;
    }

    setSaving(true);
    const finalStatus = publishStatus || status;

    const articleData = {
      title: title.trim(),
      slug: slug.trim(),
      content,
      excerpt: excerpt.trim(),
      cover_image: coverImage,
      category,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      author: author.trim(),
      status: finalStatus,
    };

    if (finalStatus === 'published' && !isEditing) {
      articleData.published_at = new Date().toISOString();
    }

    let result;

    if (isEditing) {
      result = await supabase
        .from('articles')
        .update(articleData)
        .eq('id', id)
        .select()
        .single();
    } else {
      result = await supabase
        .from('articles')
        .insert(articleData)
        .select()
        .single();
    }

    if (result.error) {
      alert('Save failed: ' + result.error.message);
      setSaving(false);
      return;
    }

    if (!isEditing && result.data) {
      setArticleId(result.data.id);
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);

    if (!isEditing && result.data) {
      navigate(`/spc-portal-1003/articles/${result.data.id}/edit`, { replace: true });
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Loading...">
        <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading article...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? 'Edit Article' : 'New Article'}>
      {/* Top Action Bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 28, paddingBottom: 20, borderBottom: `1px solid ${C.creamWarm}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            padding: '4px 12px', fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
            textTransform: 'uppercase', borderRadius: 2,
            background: status === 'published' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
            color: status === 'published' ? '#16a34a' : '#ca8a04',
          }}>
            {status}
          </span>
          {saved && (
            <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Saved!</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => navigate('/spc-portal-1003')}
            style={{
              padding: '10px 24px', border: `1px solid ${C.creamWarm}`, background: C.white,
              color: C.textBody, fontFamily: "'Manrope', sans-serif", fontSize: 12,
              fontWeight: 600, letterSpacing: 1, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => saveArticle('draft')}
            disabled={saving}
            style={{
              padding: '10px 24px', border: `1px solid ${C.navy}`, background: C.white,
              color: C.navy, fontFamily: "'Manrope', sans-serif", fontSize: 12,
              fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={() => saveArticle('published')}
            disabled={saving}
            style={{
              padding: '10px 24px', border: 'none', background: C.blue,
              color: C.navy, fontFamily: "'Manrope', sans-serif", fontSize: 12,
              fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            {saving ? 'Publishing...' : 'Publish'}
          </button>
          {isEditing && (
            <button
              onClick={() => navigate(`/spc-portal-1003/articles/${id}/preview`)}
              style={{
                padding: '10px 24px', border: `1px solid ${C.blue}`, background: 'transparent',
                color: C.blue, fontFamily: "'Manrope', sans-serif", fontSize: 12,
                fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              Preview
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>
        {/* Left — Main Content */}
        <div>
          {/* Title */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Article Title *</label>
            <input
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Enter article title"
              style={{ ...inputStyle, fontSize: 18, fontWeight: 600 }}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </div>

          {/* Slug */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>URL Slug *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <span style={{
                padding: '14px 12px', background: C.creamWarm, border: `1px solid ${C.creamWarm}`,
                borderRight: 'none', fontSize: 13, color: C.textMuted,
                fontFamily: "'Manrope', sans-serif",
              }}>
                /articles/
              </span>
              <input
                value={slug}
                onChange={(e) => setSlug(generateSlug(e.target.value))}
                placeholder="article-url-slug"
                style={{ ...inputStyle, borderLeft: 'none' }}
                onFocus={focusHandler}
                onBlur={blurHandler}
              />
            </div>
          </div>

          {/* Rich Text Editor */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Article Content</label>
            <RichTextEditor content={content} onChange={setContent} />
          </div>

          {/* File Attachments */}
          {articleId && (
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>File Attachments</label>
              <FileUploader articleId={articleId} files={files} onFilesChange={setFiles} />
            </div>
          )}
          {!articleId && (
            <div style={{
              padding: '24px', background: C.creamPale, border: `1px dashed ${C.creamWarm}`,
              textAlign: 'center', color: C.textMuted, fontSize: 13, marginBottom: 24,
            }}>
              Save the article first to enable file attachments
            </div>
          )}
        </div>

        {/* Right — Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Cover Image */}
          <div style={{ background: C.white, border: `1px solid ${C.creamWarm}`, padding: 24 }}>
            <label style={labelStyle}>Cover Image</label>
            {coverImage ? (
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <img src={coverImage} alt="Cover" style={{ width: '100%', height: 180, objectFit: 'cover', border: `1px solid ${C.creamWarm}` }} />
                <button
                  onClick={() => setCoverImage('')}
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
                    fontSize: 14, cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div
                onClick={uploadCoverImage}
                style={{
                  border: `2px dashed ${C.creamWarm}`, background: C.creamPale,
                  padding: '32px 16px', textAlign: 'center', cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ fontSize: 13, color: C.textBody, fontWeight: 500, marginBottom: 4 }}>Click to upload</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>JPG, PNG, WebP — Max 5MB</div>
              </div>
            )}
          </div>

          {/* Excerpt */}
          <div style={{ background: C.white, border: `1px solid ${C.creamWarm}`, padding: 24 }}>
            <label style={labelStyle}>Excerpt</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Brief summary for article cards..."
              style={{ ...inputStyle, resize: 'none', height: 80 }}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </div>

          {/* Category */}
          <div style={{ background: C.white, border: `1px solid ${C.creamWarm}`, padding: 24 }}>
            <label style={labelStyle}>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', color: category ? C.textDark : C.textMuted }}
            >
              <option value="">Select category</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Tags */}
          <div style={{ background: C.white, border: `1px solid ${C.creamWarm}`, padding: 24 }}>
            <label style={labelStyle}>Tags</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="steel, iron, energy (comma separated)"
              style={inputStyle}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </div>

          {/* Author */}
          <div style={{ background: C.white, border: `1px solid ${C.creamWarm}`, padding: 24 }}>
            <label style={labelStyle}>Author</label>
            <input
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Author name"
              style={inputStyle}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}